import type { LoginResponse, MeResponse, SecondFactorChallenge } from '@epc/contracts';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Fingerprint, ScanFace } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { DemoEntry } from '../../components/Demo';
import { SecondFactorStep } from '../../components/SecondFactor';
import { ApiError, api } from '../../lib/api';
import {
  autofillPasskey,
  biometryName,
  cancelPasskeyRequest,
  deviceHasPasskey,
  isCancel,
  loginLabel,
  loginWithPasskey,
  passkeyError,
  passkeysSupported,
} from '../../lib/passkeys';
import { useSetMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { ErrorBox } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const navigate = useNavigate();
  // Куда вернуться после входа (адрес, с которого отправили на вход; проверен в маршруте /login) и почта,
  // подставленная после восстановления пароля (ДС-19)
  const search = useSearch({ strict: false }) as { next?: string; email?: string };
  const next = search.next;
  const setMe = useSetMe();
  const [email, setEmail] = useState(search.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // Пароль верный — нужен код второго фактора (ДС-12)
  const [challenge, setChallenge] = useState<SecondFactorChallenge['secondFactor'] | null>(null);
  const [bioBusy, setBioBusy] = useState(false);
  const [supported] = useState(passkeysSupported);
  const [onDevice] = useState(deviceHasPasskey);
  // После восстановления пароля — сразу форма пароля, даже если на устройстве есть ключ входа
  const [showPassword, setShowPassword] = useState(!onDevice || Boolean(search.email));
  const bio = biometryName();
  const BioIcon = bio === 'Face ID' ? ScanFace : Fingerprint;

  function done(me: MeResponse) {
    setMe(me);
    navigate({ to: next ?? '/' });
  }

  // Ключ портала в подсказке поля «Почта» (iPhone, Safari, Chrome)
  // biome-ignore lint/correctness/useExhaustiveDependencies: запрос подсказки запускается один раз при открытии экрана
  useEffect(() => {
    if (!supported) return;
    let active = true;
    autofillPasskey()
      .then((me) => {
        if (active && me) done(me);
      })
      .catch(() => {});
    return () => {
      active = false;
      cancelPasskeyRequest();
    };
  }, [supported]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<LoginResponse>('/auth/login', { body: { email, password } });
      if ('secondFactor' in r) {
        setPassword('');
        setChallenge(r.secondFactor);
      } else done(r);
    } catch (err) {
      setError(err instanceof ApiError ? err : new Error('Не удалось войти'));
    } finally {
      setBusy(false);
    }
  }

  async function biometric() {
    setBioBusy(true);
    setError(null);
    try {
      done(await loginWithPasskey());
    } catch (err) {
      if (!isCancel(err)) setError(passkeyError(err));
    } finally {
      setBioBusy(false);
    }
  }

  const bioButton = supported ? (
    <Button
      type="button"
      variant={onDevice ? 'primary' : 'secondary'}
      size="lg"
      loading={bioBusy}
      icon={<BioIcon className="size-5" />}
      onClick={biometric}
    >
      {loginLabel()}
    </Button>
  ) : null;

  if (challenge)
    return (
      <AuthLayout
        title={challenge.enroll ? 'Защита входа' : 'Код подтверждения'}
        subtitle={
          challenge.enroll
            ? 'Подключите второй фактор — это делается один раз.'
            : 'Пароль верный. Подтвердите вход кодом.'
        }
      >
        <SecondFactorStep challenge={challenge} onDone={done} onCancel={() => setChallenge(null)} />
      </AuthLayout>
    );

  return (
    <AuthLayout
      title="Вход в портал"
      subtitle={
        onDevice ? 'Вход без пароля — по биометрии этого устройства.' : 'Войдите рабочей почтой и паролем.'
      }
    >
      <div className="flex flex-col gap-5">
        {onDevice ? bioButton : null}
        {onDevice && !showPassword ? (
          <>
            <ErrorBox error={error} />
            <button
              type="button"
              className="self-start text-[14px] font-medium text-ink-2 underline-offset-4 hover:text-ink hover:underline"
              onClick={() => setShowPassword(true)}
            >
              Войти по почте и паролю
            </button>
          </>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
            <Field label="Почта" htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="username webauthn"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field label="Пароль" htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
            {/* Восстановление пароля по почте (ДС-19): введённая почта переносится на страницу запроса ссылки */}
            <Link
              to="/forgot-password"
              search={{ email: email.trim() || undefined }}
              className="-mt-3 inline-flex min-h-11 items-center self-start text-[14px] font-medium text-ink-2 underline-offset-4 hover:text-ink hover:underline"
            >
              Забыли пароль?
            </Link>
            <ErrorBox error={error} />
            <Button
              type="submit"
              variant={onDevice ? 'secondary' : 'primary'}
              size="lg"
              loading={busy}
              disabled={!email || !password}
            >
              Войти
            </Button>
            {!onDevice ? bioButton : null}
            <p className="text-[13px] text-ink-2">
              {supported && !onDevice
                ? `Вход по ${bio} включается после первого входа по паролю — портал предложит это сам. `
                : ''}
              Нет доступа? Попросите администратора прислать приглашение на вашу почту.
            </p>
          </form>
        )}
        {/* Демо (ГЛ-03): посмотреть портал на вымышленной компании, не регистрируясь */}
        <DemoEntry className="border-t border-line pt-5" />
        {/* Новой компании (ГЛ-04): заявка без входа, владелец портала создаст компанию и пришлёт приглашение */}
        <p className="border-t border-line pt-5 text-[13px] text-ink-2">
          Вашей компании ещё нет в портале?{' '}
          <Link
            to="/request-access"
            className="inline-flex min-h-11 items-center font-medium text-ink underline underline-offset-4"
          >
            Запросить доступ
          </Link>
          {' · '}
          <Link
            to="/welcome"
            className="inline-flex min-h-11 items-center font-medium text-ink underline underline-offset-4"
          >
            О портале
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
